import { clipAuditText } from './audit';

const toFiniteNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const clampPositive = (value, fallback) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return fallback;
  return numeric;
};

const normalizeContextText = (value = '') => {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
};

const buildSourcePriorityMap = (sourcePriorities = []) => {
  const map = new Map();
  for (const entry of Array.isArray(sourcePriorities) ? sourcePriorities : []) {
    const source = String(entry?.source || '').trim();
    if (!source || map.has(source)) continue;
    map.set(source, clampPositive(entry?.priority, 99));
  }
  return map;
};

export const calculateRecencyBoost = ({ rank = 0, recencyWeight = 0.35, recencyDecayTurns = 8 } = {}) => {
  const safeRank = Math.max(0, Number(rank || 0));
  const safeWeight = Math.max(0, Number(recencyWeight || 0));
  const safeDecay = Math.max(1, Number(recencyDecayTurns || 8));
  return safeWeight * Math.exp(-safeRank / safeDecay);
};

export const rankAndBudgetContextItems = ({
  items = [],
  sourcePriorities = [],
  maxContextItems = 14,
  maxContextChars = 12000,
  recencyWeight = 0.35,
  recencyDecayTurns = 8
} = {}) => {
  const priorityMap = buildSourcePriorityMap(sourcePriorities);
  const deduped = new Map();

  for (const item of Array.isArray(items) ? items : []) {
    const text = normalizeContextText(item?.text || '');
    if (!text) continue;

    const source = String(item?.source || 'unknown').trim() || 'unknown';
    const baseScore = toFiniteNumber(item?.baseScore, 0);
    const recencyRank = Math.max(0, Number(item?.recencyRank || 0));
    const recencyBoost = toFiniteNumber(item?.recencyBoost, calculateRecencyBoost({
      rank: recencyRank,
      recencyWeight,
      recencyDecayTurns
    }));
    const sourcePriority = priorityMap.get(source) || 99;
    const finalScore = baseScore + recencyBoost;

    const key = `${source}::${text.toLowerCase()}`;
    const next = {
      ...item,
      source,
      text,
      baseScore,
      recencyBoost,
      recencyRank,
      sourcePriority,
      score: finalScore
    };

    const existing = deduped.get(key);
    const existingRole = String(existing?.meta?.contextRole || '').trim().toLowerCase();
    const nextRole = String(next?.meta?.contextRole || '').trim().toLowerCase();
    const preserveExistingStyleReference = existingRole === 'style_reference' && nextRole !== 'style_reference';
    if ((!existing || next.score > existing.score) && !preserveExistingStyleReference) {
      deduped.set(key, next);
    }
  }

  const ranked = Array.from(deduped.values()).sort((left, right) => {
    if (right.score !== left.score) return right.score - left.score;
    if (left.sourcePriority !== right.sourcePriority) return left.sourcePriority - right.sourcePriority;
    return left.text.localeCompare(right.text);
  });

  const boundedItems = [];
  const itemLimit = Math.max(1, Number(maxContextItems || 14));
  const charLimit = Math.max(64, Number(maxContextChars || 12000));
  let usedChars = 0;

  for (const item of ranked) {
    if (boundedItems.length >= itemLimit) break;
    const remainingChars = charLimit - usedChars;
    if (remainingChars <= 16) break;

    const fullText = String(item.text || '').trim();
    if (!fullText) continue;

    const nextText = fullText.length <= remainingChars
      ? fullText
      : `${fullText.slice(0, Math.max(0, remainingChars - 3)).trimEnd()}...`;

    if (!nextText) continue;

    boundedItems.push({
      ...item,
      text: nextText,
      truncated: nextText.length < fullText.length
    });
    usedChars += nextText.length;
  }

  const summarizeCandidate = (item = {}) => ({
    source: String(item?.source || 'unknown').trim() || 'unknown',
    docId: Number(item?.meta?.docId || 0) || null,
    docName: item?.meta?.docName || item?.meta?.label || null,
    chunkId: item?.meta?.chunkHash || null,
    score: Number(toFiniteNumber(item?.score, 0).toFixed(3)),
    baseScore: Number(toFiniteNumber(item?.baseScore, 0).toFixed(3)),
    recencyBoost: Number(toFiniteNumber(item?.recencyBoost, 0).toFixed(3)),
    contextRole: item?.meta?.contextRole || null,
    trigger: item?.meta?.trigger || null,
    matchedKeyword: item?.meta?.matchedKeyword || null,
    keywordHitUniqueCount: Number(item?.meta?.keywordHitUniqueCount || 0) || 0,
    keywordHitTotalCount: Number(item?.meta?.keywordHitTotalCount || 0) || 0,
    textChars: String(item?.text || '').length,
    piggybackFromChunkId: item?.meta?.piggybackFromChunkId || null,
    piggybackReason: item?.meta?.piggybackReason || null,
    query: item?.meta?.query || null,
    textPreview: clipAuditText(item?.text || '', 180)
  });

  const selectedKeys = new Set(
    boundedItems.map((item) => `${String(item?.source || '').trim()}::${String(item?.text || '').toLowerCase()}`)
  );
  const droppedCandidates = ranked.filter((item) => {
    const key = `${String(item?.source || '').trim()}::${String(item?.text || '').toLowerCase()}`;
    return !selectedKeys.has(key);
  });

  return {
    items: boundedItems,
    meta: {
      totalCandidates: ranked.length,
      selectedCount: boundedItems.length,
      selectedChars: usedChars,
      maxContextItems: itemLimit,
      maxContextChars: charLimit,
      selectionAudit: {
        selected: boundedItems.slice(0, 14).map(summarizeCandidate),
        dropped: droppedCandidates.slice(0, 14).map(summarizeCandidate),
        droppedCount: droppedCandidates.length
      }
    }
  };
};